/* globals views, globalNavigate, showModalConfirm, showModalError */
import React, {useContext, useState, useEffect} from "react";
import styled from "styled-components";
import Request from "common/Request";
import ActionsContext from "common/ActionsContext";
import { createPortal } from 'react-dom';
import { EditQuotasModal } from "./EditQuotaModal";
import BqnContext from "common/BqnContext";
import Table from "common/graphs/Table/Table";
import { TextWithIcon } from "common/graphs/Table/TableRenders";
import Modal from "common/Modal";
import {parseUseAndLimitVolume, formatVolumeInGBs} from "../api";

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  padding-top: 10px;
`;

const returnTo = "viewSubscriberQuotas";

const openSubscriberDetails = (target) => {
  views.doKeep(returnTo);
  globalNavigate("viewSubsDetail", {returnView: returnTo, ...target});
};

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.flatMap((ipAddress) => [
      `api subscriber ${ipAddress} quota volume disabled`,
      `api subscriber ${ipAddress} quota time disabled`,
    ]);
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError(error, ""));
  };
  if (!showDeleteModal) {
    return null;
  }
  const title =
    items.length === 1
      ? `Eliminate time and volume quotas for IP Address ${items[0]}?`
      : `Eliminate time and volume quotas for ${items.length} IP Addresses?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={items.length > 1 && items.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {items.length > 1 && items.length < 100 ? (
            <ul>
              {items.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const getColumns = ({
  includeBqn,
  setShowEditQuotasModal,
  reloadWhenDone,
  setDeleteSelected,
  setShowDeleteModal,
}) => {
  return [
    ...(includeBqn
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
            title: "BQN ID",
          },
        ]
      : []),
      {
        label: "IP-ADDRESS",
        id: "ADDRESS",
        title: "Subscriber’s IP address",
        numeric: false,
        cellClassName: "hyperlink-text",
        onClick:(addr) => openSubscriberDetails({ addr }),
      },
      {
        label: "SUBSCRIBER-ID",
        id: "SUBSCRIBER-ID",
        title: "Subscriber ID",
        numeric: false,
        cellClassName: "hyperlink-text",
        onClick:(subsId) => openSubscriberDetails({ subsId }),
      },
      {
        label: "QUOTA-BLOCK",
        id: "QUOTA",
        title: "QUOTA-BLOCK",
        renderCell: (value) =>  {
          const valueDisplay = 
            value === "n/a" ? "n/a" :
            value === "blocked" ? "blocked" : "no";
          const className = value !== "n/a" ? "hyperlink-text" : "";
          return <TextWithIcon value={valueDisplay} className={className} />;
        },
        parse: (value) =>
          value === "n/a" ? value : value === "blocked" ? "yes" : "no",
      },
      {
        label: "QUOTA-TIME-LIMIT",
        id: "LIMIT-TIME",
        title: "QUOTA-TIME-LIMIT",
        numeric: false,
        parse: (data) => (data === "n/a" ? null : new Date(`${data}`)),
      },
      {
        label: "QUOTA-GBYTES-USED",
        id: "VOLUME-USED",
        title: "QUOTA-GBYTES-USED",
        numeric: false,
        renderCell: formatVolumeInGBs,
      },
      {
        label: "QUOTA-GBYTE-LIMIT",
        id: "VOLUME-LIMIT",
        title: "QUOTA-GBYTE-LIMIT",
        numeric: false,
        renderCell: formatVolumeInGBs,
      },
      {
        label: "ACTIONS",
        id: "ACTIONS",
        actions: [
          {
            label: "Edit",
            icon: "edit",
            onClick: (row) => {
              setShowEditQuotasModal({
                show: true,
                params: {
                  ...( 
                    ( 
                      ([currentVolumeUsed, currentVolumeLimit]) =>
                       ({currentVolumeUsed, currentVolumeLimit})
                    )(parseUseAndLimitVolume(row["LIMIT-VOLUME"]))
                  ),
                  ipAddress: row["ADDRESS"],
                  currentTimeLimit: row["LIMIT-TIME"],
                },
              });
            },
          },
          {
            label: "Delete",
            icon: "delete",
            onClick: (row) => {
              setDeleteSelected([row["ADDRESS"]]);
              setShowDeleteModal(true);
            },
          },
        ],
      },
  ];
}

const ListSubscribers = ({ request}) => {
  const actions = useContext(ActionsContext);
  const reloadWhenDone = () => actions.send("do-load");
  const [showEditQuotasModal, setShowEditQuotasModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  useEffect(() => 
    actions.recv('create-quota', () => setShowEditQuotasModal({show: true}))
  , [])
  
  return (
    <>
      <TableContainer>
        <Request during={request}>
          {({ response }) => {
            const [header, ...rows] = response.trim().split("\n");
            const headerAttrs = header.trim().split(/\s+/);
            const newRows = rows.map((row) => {
              const rowAttrs = row.trim(/\s/).split(/\s+/);
              const newEntry = rowAttrs.reduce((acc, attr, index) => {
                acc[headerAttrs[index]] = attr;
                return acc;
              }, {});
              const [volumeUsed, volumeLimit] = parseUseAndLimitVolume(
                newEntry["LIMIT-VOLUME"]
              );
              return {
                ...newEntry,
                "VOLUME-USED": volumeUsed,
                "VOLUME-LIMIT": volumeLimit,
              };
            });
            const filteredRows = newRows
              .filter((row) => {
                const bqn = row["BQN"] || null;
                return (
                  row["QUOTA"] !== "n/a" &&
                  (bqn === null ||
                    bqnId === "all" ||
                    bqn.toString() === bqnId.toString())
                );
              })
              .map((row, index) => ({
                ...row,
                id: row.id !== undefined ? row.id : index,
              }));

            return (
              <Table
                isSelectable={true}
                isRowSelectable={false}
                data={filteredRows}
                sort={true}
                columns={getColumns({
                  includeBqn: mode === "cluster",
                  setShowEditQuotasModal,
                  reloadWhenDone,
                  setDeleteSelected,
                  setShowDeleteModal,
                })}
                search={true}
                csvPrefix="subscriber-quotas"
                rowsPerPageNr={50}
                selected={selected}
                setSelected={setSelected}
                deleteSelected={(values) => {
                  const selectedItems = filteredRows
                    .filter((row) => values.includes(row.id))
                    .map((row) => row["ADDRESS"]);
                  setDeleteSelected(selectedItems);
                  setShowDeleteModal(true);
                }}
              />
            );
          }}
        </Request>
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          reloadWhenDone();
        }}
      />
      {showEditQuotasModal &&
        showEditQuotasModal.show &&
        createPortal(
          <EditQuotasModal
            onOk={reloadWhenDone}
            onClose={() => setShowEditQuotasModal({ show: false })}
            params={showEditQuotasModal.params}
          />,
          document.body,
        )}
    </>
  ); 
}

export default ListSubscribers;
